// src/components/meetings/MeetingList.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, MapPin, User, Search, Filter, 
  ChevronDown, CheckCircle, AlertCircle, Plus, Building, Users
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { AppContext } from '../../context/AppContext';
import { db } from '../../firebase/config';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  limit 
} from 'firebase/firestore';

const MeetingList = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  
  const [meetings, setMeetings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'upcoming', 'past'
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Business meeting types for filtering
  const meetingTypes = [
    'Board Meeting',
    'Team Meeting', 
    'Department Meeting',
    'All Hands',
    'Project Review',
    'Budget Review',
    'Strategy Session',
    'Client Meeting',
    'Vendor Meeting',
    'Training Session',
    'Performance Review',
    'Planning Session',
    'Other'
  ];

  const priorityLevels = ['low', 'medium', 'high', 'urgent'];

  // Fetch meetings from Firestore
  useEffect(() => {
    const fetchMeetings = async () => {
      if (!activeBusiness?.id) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError('');

      try {
        const meetingsRef = collection(db, "meetings");
        
        // Query based on businessId
        const q = query(
          meetingsRef,
          where("businessId", "==", activeBusiness.id),
          orderBy("date", "desc"),
          limit(50)
        );

        const querySnapshot = await getDocs(q);
        const meetingsList = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          meetingsList.push({
            id: doc.id,
            ...data,
            date: data.date?.toDate() || null,
            endTime: data.endTime?.toDate() || null,
            createdAt: data.createdAt?.toDate() || null,
            updatedAt: data.updatedAt?.toDate() || null
          });
        });

        setMeetings(meetingsList);
      } catch (err) {
        console.error("Error fetching meetings:", err);
        setError("Failed to load meetings. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeetings();
  }, [activeBusiness]);

  // Filter meetings based on search term and filters
  const filteredMeetings = meetings.filter(meeting => {
    const matchesSearch = meeting.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          meeting.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          meeting.department?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const now = new Date();
    const isPast = meeting.date && meeting.date < now;
    
    const matchesDateFilter = 
      filter === 'all' || 
      (filter === 'upcoming' && (!meeting.date || !isPast)) ||
      (filter === 'past' && meeting.date && isPast);
    
    const matchesTypeFilter = !typeFilter || meeting.type === typeFilter;
    const matchesPriorityFilter = !priorityFilter || meeting.priority === priorityFilter;
    
    return matchesSearch && matchesDateFilter && matchesTypeFilter && matchesPriorityFilter;
  });

  // Format date time nicely
  const formatDateTime = (date) => {
    if (!date) return '';
    return format(date, 'MMM d, yyyy • h:mm a');
  };

  // Get status badge details
  const getStatusDetails = (meeting) => {
    if (!meeting.date) {
      return {
        label: 'Not Scheduled',
        color: 'bg-gray-100 text-gray-800',
        icon: <Calendar className="h-4 w-4 mr-1" />
      };
    }
    
    const now = new Date();
    const meetingDate = new Date(meeting.date);
    
    if (meetingDate < now) {
      return {
        label: 'Completed',
        color: 'bg-gray-100 text-gray-800',
        icon: <CheckCircle className="h-4 w-4 mr-1" />
      };
    }
    
    // Check if meeting is happening today
    const today = new Date();
    const isToday = meetingDate.getDate() === today.getDate() &&
                    meetingDate.getMonth() === today.getMonth() &&
                    meetingDate.getFullYear() === today.getFullYear();
                    
    if (isToday) {
      return {
        label: 'Today',
        color: 'bg-green-100 text-green-800',
        icon: <AlertCircle className="h-4 w-4 mr-1" />
      };
    }
    
    // Calculate days until meeting
    const diffTime = Math.abs(meetingDate - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 3) {
      return {
        label: 'Upcoming',
        color: 'bg-yellow-100 text-yellow-800',
        icon: <Clock className="h-4 w-4 mr-1" />
      };
    }
    
    return {
      label: 'Scheduled',
      color: 'bg-blue-100 text-blue-800',
      icon: <Calendar className="h-4 w-4 mr-1" />
    };
  };

  // Get priority badge details
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'urgent':
        return { label: 'Urgent', color: 'bg-purple-100 text-purple-800' };
      case 'high':
        return { label: 'High', color: 'bg-red-100 text-red-800' };
      case 'medium':
        return { label: 'Medium', color: 'bg-yellow-100 text-yellow-800' };
      case 'low':
        return { label: 'Low', color: 'bg-green-100 text-green-800' };
      default:
        return { label: 'Normal', color: 'bg-gray-100 text-gray-800' };
    }
  };

  // Handle meeting selection
  const handleSelectMeeting = (meetingId) => {
    navigate(`/meetings/details/${meetingId}`);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#190192]"></div>
        <p className="ml-2">Loading meetings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meetings</h1>
          {activeBusiness && (
            <p className="mt-1 text-sm text-gray-500 flex items-center">
              <Building className="h-4 w-4 mr-1" />
              {activeBusiness.name}
            </p>
          )}
        </div>
        <button
          onClick={() => navigate('/meetings/create')}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-blue-700"
        >
          <Plus size={16} className="mr-2" />
          New Meeting
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search meetings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
          >
            <Filter size={16} className="mr-2" />
            Filters
            <ChevronDown size={16} className={`ml-1 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date Filter</label>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Meetings</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="past">Past</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Meeting Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Types</option>
                  {meetingTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Priorities</option>
                  {priorityLevels.map(priority => (
                    <option key={priority} value={priority}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setFilter('all');
                    setTypeFilter('');
                    setPriorityFilter('');
                  }}
                  className="w-full px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Meetings List */}
      {filteredMeetings.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No meetings found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || typeFilter || priorityFilter || filter !== 'all' 
              ? 'Try changing your search or filter criteria' 
              : 'Get started by creating a new meeting'}
          </p>
          {!searchTerm && !typeFilter && !priorityFilter && filter === 'all' && (
            <button
              onClick={() => navigate('/meetings/create')}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-blue-700"
            >
              <Plus size={16} className="mr-2" />
              Create First Meeting
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-gray-200">
          <ul className="divide-y divide-gray-200">
            {filteredMeetings.map((meeting) => {
              const statusDetails = getStatusDetails(meeting);
              const priorityBadge = getPriorityBadge(meeting.priority);
              
              return (
                <li 
                  key={meeting.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleSelectMeeting(meeting.id)}
                >
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-medium text-gray-900 truncate">{meeting.title}</h3>
                          
                          {/* Status Badge */}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusDetails.color}`}>
                            {statusDetails.icon}
                            {statusDetails.label}
                          </span>

                          {/* Priority Badge */}
                          {meeting.priority && meeting.priority !== 'medium' && (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityBadge.color}`}>
                              {priorityBadge.label}
                            </span>
                          )}
                        </div>

                        <div className="mt-1 flex flex-wrap items-center text-sm text-gray-500 gap-x-4 gap-y-1">
                          <div className="flex items-center">
                            <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {formatDateTime(meeting.date)}
                          </div>
                          {meeting.location && (
                            <div className="flex items-center">
                              <MapPin className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              {meeting.location}
                            </div>
                          )}
                          {meeting.chairperson && (
                            <div className="flex items-center">
                              <User className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              {meeting.chairperson}
                            </div>
                          )}
                          {meeting.department && (
                            <div className="flex items-center">
                              <Building className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              {meeting.department}
                            </div>
                          )}
                          {meeting.participants && meeting.participants.length > 0 && (
                            <div className="flex items-center">
                              <Users className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              {meeting.participants.length} participant{meeting.participants.length !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>

                        {meeting.type && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {meeting.type}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MeetingList;